import { Injectable } from "@angular/core";
import { catchError, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { TrafficChief, AdminTrafficChiefCreationRequestDTO, TrafficChiefRequest } from "./traffic-chief.interface";

@Injectable()
export class TrafficChiefService {

    baseUrl = environment.administration_url + '/admin/traffic-chiefs';
    appUrl = environment.administration_url + '/app/traffic-chiefs';
    private _trafficChief: ReplaySubject<TrafficChief> = new ReplaySubject<TrafficChief>(1);
    private _trafficChiefs: ReplaySubject<TrafficChief[]> = new ReplaySubject<TrafficChief[]>(1);
    private _metadata: ReplaySubject<any> = new ReplaySubject<any>(1);

    set trafficChief(value: TrafficChief) {
        this._trafficChief.next(value);
    }

    get trafficChief$() {
        return this._trafficChief.asObservable();
    }

    set trafficChiefs(value: TrafficChief[]) {
        this._trafficChiefs.next(value);
    }

    get trafficChiefs$() {
        return this._trafficChiefs.asObservable();
    }

    set metadata(value: any) {
        this._metadata.next(value);
    }

    get metadata$() {
        return this._metadata.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }

    getAll(filters?: { page?: number; size?: number; keyword?: string }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.page !== undefined) params = params.set('page', filters.page.toString());
            if (filters.size !== undefined) params = params.set('size', filters.size.toString());
            if (filters.keyword) params = params.set('keyword', filters.keyword);
        }
        return this._httpClient.get<any>(`${this.baseUrl}`, { params })
        .pipe(
            tap((response: any) => {
                this.trafficChiefs = response?.content || [];
                this.metadata = response;
                return response;
            }),
            catchError(() => of({ content: [] }))
        );
    }

    getById(uuid: string): Observable<TrafficChief> {
        return this._httpClient.get<TrafficChief>(`${this.appUrl}/${uuid}`)
        .pipe(
            tap((chief) => {
                this.trafficChief = chief;
                return chief;
            }),
            catchError(() => of({} as TrafficChief))
        );
    }

    createWithUser(dto: AdminTrafficChiefCreationRequestDTO): Observable<TrafficChief> {
        return this._httpClient.post<TrafficChief>(`${this.baseUrl}-and-users`, dto)
        .pipe(
            tap((chief) => {
                this.trafficChief = chief;
                return chief;
            })
        );
    }

    update(uuid: string, data: TrafficChiefRequest): Observable<TrafficChief> {
        return this._httpClient.put<TrafficChief>(`${this.appUrl}/${uuid}`, data)
        .pipe(
            tap((chief) => {
                this.trafficChief = chief;
                return chief;
            })
        );
    }

    activate(uuid: string): Observable<TrafficChief> {
        return this._httpClient.patch<TrafficChief>(`${this.appUrl}/${uuid}/activate`, {})
        .pipe(
            tap((chief) => {
                this.trafficChief = chief;
                return chief;
            }),
            catchError(() => of({} as TrafficChief))
        );
    }

    deactivate(uuid: string): Observable<TrafficChief> {
        return this._httpClient.patch<TrafficChief>(`${this.appUrl}/${uuid}/deactivate`, {})
        .pipe(
            tap((chief) => {
                this.trafficChief = chief;
                return chief;
            }),
            catchError(() => of({} as TrafficChief))
        );
    }

    assignAgents(uuid: string, agentUuids: string[]): Observable<TrafficChief> {
        return this._httpClient.patch<TrafficChief>(`${this.appUrl}/${uuid}/assign-agents`, { agentUuids })
        .pipe(
            tap((chief) => {
                this.trafficChief = chief;
                return chief;
            }),
            catchError(() => of({} as TrafficChief))
        );
    }
}
